import React from 'react';
import { StyleSheet, TouchableOpacity, type TouchableOpacityProps, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { Icon } from '../../../../../Icon';
import { BodyText } from '../../../../../Text';
import { NAVBAR_FLOATING_ACTION_SIZE } from '../../../../Navbar.constants';
import { NavbarGlassView } from '../NavbarGlassView/NavbarGlassView';

// ─────────────────────────────────────────────────────────────────────────────

export const NavbarFloatingBagButton = (props: Props) => {
  const { count = 0, shadow, onPress, ...restProps } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const isPopulated = count > 0;
  const displayedCount = count > MAX_DISPLAYED_COUNT ? `${MAX_DISPLAYED_COUNT}+` : count;
  const iconColor = isPopulated ? theme.colors.WHITE : theme.colors.KALE;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TouchableOpacity {...restProps} onPress={onPress} accessibilityRole="link" activeOpacity={0.7}>
      <NavbarGlassView canHover shadow={shadow} style={styles.container}>
        {isPopulated ? (
          <Animated.View
            entering={BAG_FILL_ENTERING}
            exiting={BAG_FILL_EXITING}
            pointerEvents="none"
            style={styles.fillOverlay}
          />
        ) : undefined}

        <View style={styles.content}>
          <View style={styles.iconContainer}>
            <Icon color={iconColor} height={28} name="IconBagFill" width={28} />

            {isPopulated ? (
              <BodyText maxFontSizeMultiplier={1} sizeMatch={['14']} style={styles.count}>
                {displayedCount}
              </BodyText>
            ) : undefined}
          </View>
        </View>
      </NavbarGlassView>
    </TouchableOpacity>
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

const MAX_DISPLAYED_COUNT = 9;
const BAG_FILL_ENTERING = FadeIn.duration(theme.transitions.short);
const BAG_FILL_EXITING = FadeOut.duration(theme.transitions.short);

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: NAVBAR_FLOATING_ACTION_SIZE,
    height: NAVBAR_FLOATING_ACTION_SIZE,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fillOverlay: {
    position: 'absolute',
    top: -1,
    right: -1,
    left: -1,
    bottom: -1,
    borderRadius: 1000,
    backgroundColor: theme.colors.KALE,
  },
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  count: {
    position: 'absolute',
    top: 8,
    color: theme.colors.KALE,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  count?: number;
  shadow?: boolean;
} & Pick<TouchableOpacityProps, 'accessibilityLabel' | 'onPress'>;
